"use client";

import { type ReactNode, useCallback, useMemo, useState } from "react";
import { cn } from "@/lib/utils";
import type { ChartBrushSelection } from "./chart-brush";

export type ChartBrushLayoutRenderProps<T extends Record<string, unknown>> = {
  brushSelection: ChartBrushSelection | null;
  onBrushSelectionChange: (selection: ChartBrushSelection | null) => void;
  xDomain?: [Date, Date];
  xDomainSlotCount: number;
  filteredData: T[];
};

export interface ChartBrushLayoutProps<T extends Record<string, unknown>> {
  data: T[];
  xDataKey?: keyof T & string;
  enabled?: boolean;
  initialSelection?: ChartBrushSelection | null;
  onSelectionChange?: (selection: ChartBrushSelection | null) => void;
  brushStrip?: (props: ChartBrushLayoutRenderProps<T>) => ReactNode;
  children: (props: ChartBrushLayoutRenderProps<T>) => ReactNode;
  className?: string;
  stripClassName?: string;
}

function coerceDate(v: unknown): Date | null {
  if (v instanceof Date) return v;
  if (typeof v === "string") {
    const d = new Date(v);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  if (typeof v === "number") return new Date(v);
  return null;
}

export function ChartBrushLayout<T extends Record<string, unknown>>({
  data,
  xDataKey,
  enabled = true,
  initialSelection = null,
  onSelectionChange,
  brushStrip,
  children,
  className,
  stripClassName,
}: ChartBrushLayoutProps<T>) {
  const [brushSelection, setBrushSelection] =
    useState<ChartBrushSelection | null>(initialSelection);

  const handleBrushSelectionChange = useCallback(
    (selection: ChartBrushSelection | null) => {
      setBrushSelection(selection);
      onSelectionChange?.(selection);
    },
    [onSelectionChange],
  );

  const xDomain = useMemo<[Date, Date] | undefined>(() => {
    if (!brushSelection || brushSelection.kind !== "time") return undefined;
    return [brushSelection.start, brushSelection.end];
  }, [brushSelection]);

  const filteredData = useMemo(() => {
    if (!brushSelection) return data;
    if (brushSelection.kind === "time") {
      if (!xDataKey) return data;
      const s = brushSelection.start.getTime();
      const e = brushSelection.end.getTime();
      return data.filter((d) => {
        const dt = coerceDate(d[xDataKey]);
        return dt ? dt.getTime() >= s && dt.getTime() <= e : true;
      });
    }
    return data.slice(brushSelection.startIndex, brushSelection.endIndex + 1);
  }, [data, brushSelection, xDataKey]);

  const renderProps: ChartBrushLayoutRenderProps<T> = {
    brushSelection,
    onBrushSelectionChange: handleBrushSelectionChange,
    xDomain,
    xDomainSlotCount: data.length,
    filteredData,
  };

  return (
    <div className={cn("flex h-full w-full flex-col", className)}>
      {children(renderProps)}
      {enabled && brushStrip ? (
        <div className={cn("relative w-full", stripClassName)}>
          {brushStrip(renderProps)}
        </div>
      ) : null}
    </div>
  );
}

export default ChartBrushLayout;
